import { AddressVerificationState } from '@/lib/enums/AddressVerificationState'
import type { Address } from '@/lib/services/addresses/addresses.types'

import {
  type AdminAddressVerificationState,
  withAdminAddressVerificationLabel,
} from './admin-address-verification-labels'

type DeliveryAddressVerificationInput = Pick<Address, 'verification_state'> | null | undefined

export function getDeliveryAddressVerificationState(
  deliveryAddress: DeliveryAddressVerificationInput
): AdminAddressVerificationState | null {
  if (!deliveryAddress) return null

  const backendState = deliveryAddress.verification_state
  if (!backendState) return null

  if (backendState === AddressVerificationState.Verified.Value) {
    return withAdminAddressVerificationLabel(AddressVerificationState.Verified)
  }

  if (backendState === AddressVerificationState.VerifiedApproximate.Value) {
    return withAdminAddressVerificationLabel(AddressVerificationState.VerifiedApproximate)
  }

  if (backendState === AddressVerificationState.ManuallyVerified.Value) {
    return withAdminAddressVerificationLabel(AddressVerificationState.ManuallyVerified)
  }

  if (backendState === AddressVerificationState.Unverified.Value) {
    return withAdminAddressVerificationLabel(AddressVerificationState.Unverified)
  }

  return null
}

export function getDeliveryAddressVerificationTooltip(
  deliveryAddress: DeliveryAddressVerificationInput
): string | null {
  const state = getDeliveryAddressVerificationState(deliveryAddress)

  if (!state) return null

  if (state.Value === AddressVerificationState.VerifiedApproximate.Value) {
    return 'Verify address before delivery. We found this area, but not an exact building match.'
  }

  if (state.Value === AddressVerificationState.Unverified.Value) {
    return 'Verify address before delivery.'
  }

  if (state.Value === AddressVerificationState.ManuallyVerified.Value) {
    return 'Address manually verified.'
  }

  return state.Label
}

export function isDeliveryAddressExplicitlyUnverified(
  deliveryAddress: DeliveryAddressVerificationInput
): boolean {
  return (
    getDeliveryAddressVerificationState(deliveryAddress)?.Value ===
    AddressVerificationState.Unverified.Value
  )
}
